import { AnalyticsContext, AnalyticsListener } from '@atlaskit/analytics-next';
import { setGlobalTheme } from '@atlaskit/tokens/set-global-theme';
import { css } from '@emotion/react';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { IntlProvider } from '@sandbox/app-intl';
import { IntlProvider as EditorIntlProvider } from 'react-intl';
import { useHomeRoute } from '../routes';
import { InitUFO } from '../utils/ufo';
import { ThemeContext, ThemeValue } from './settings/theme-context';
import { LOCALSTORAGE_themeKey } from '../constants/storage';
import { FabricChannel } from '@atlaskit/analytics-listeners';
import { getAnalyticsWebClient } from '../utils/analytics';
import UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';

const getSavedThemePreference = () => {
  const savedThemePreference = window.localStorage.getItem(
    LOCALSTORAGE_themeKey
  );
  if (savedThemePreference === 'light') {
    return 'light';
  } else if (savedThemePreference === 'dark') {
    return 'dark';
  }
  return null;
};

export const wrapper = css`
  box-sizing: border-box;
  height: 100%;
  max-height: 100%;
`;

export const content = css`
  padding: 0;
  height: 100%;
  max-height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const AppRouteComponent: FunctionComponent = () => {
  const route = useHomeRoute();
  const Component = route?.component;
  return (
    <div css={wrapper}>
      <div css={content}>{Component ? <Component /> : null}</div>
    </div>
  );
};

export const App: FunctionComponent = () => {
  const [theme, setTheme] = useState<ThemeValue>(
    getSavedThemePreference() ?? 'light'
  );
  const analyticClient = getAnalyticsWebClient();

  const processEditorEvent = useCallback(
    (event: UIAnalyticsEvent) => {
      if (
        !event.payload ||
        !event.payload.eventType ||
        !event.payload.action ||
        !event.payload.actionSubject
      ) {
        return;
      }

      const payload = {
        ...event.payload,
        actionSubject: 'confluenceSandboxEditor',
        actionSubjectId: undefined,
        attributes: {
          ...event.payload.attributes,
          editorAction: event.payload.action,
          editorActionSubject: event.payload.actionSubject,
          editorActionSubjectId: event.payload.actionSubjectId
        },
        source: 'confluenceSandbox'
      };

      switch (event.payload.eventType) {
        case 'ui':
          return analyticClient.sendUIEvent({
            ...payload,
            action: 'used'
          });
        case 'track':
          return analyticClient.sendTrackEvent({
            ...payload,
            action: 'tracked'
          });
        case 'operational':
          return analyticClient.sendOperationalEvent({
            ...payload,
            action: 'operated'
          });
      }
    },
    [analyticClient]
  );

  useEffect(() => {
    setGlobalTheme({ colorMode: theme });
    window.localStorage.setItem(LOCALSTORAGE_themeKey, theme);
    // oxlint-disable-next-line typescript/no-explicit-any
    (window as any).setDemoTheme = (theme: ThemeValue) => {
      setTheme(theme);
    };
  }, [theme]);

  return (
    <>
      <InitUFO />
      <IntlProvider locale={'en'}>
        <EditorIntlProvider locale={'en'}>
          <ThemeContext.Provider value={{ theme, setTheme }}>
            <AnalyticsListener
              channel={FabricChannel.editor}
              onEvent={processEditorEvent}
            >
              <AnalyticsContext
                data={{
                  pageKey: 'home'
                }}
              >
                <AppRouteComponent />
              </AnalyticsContext>
            </AnalyticsListener>
          </ThemeContext.Provider>
        </EditorIntlProvider>
      </IntlProvider>
    </>
  );
};
